// src/Components/EmojiPicker.js
import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import { EmojiEmotions as EmojiIcon } from '@mui/icons-material';

// Emoji categories with common emojis
const EMOJI_CATEGORIES = {
  smileys: {
    label: 'Smileys',
    emojis: ['😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂', '🙂', '😊', '😇', '🥰', '😍', '🤩', '😘', '😗', '😚', '😋', '😛', '😜', '🤪', '😝', '🤗', '🤭', '🤫', '🤔', '🤐', '🤨', '😐', '😑', '😶', '😏', '😒', '🙄', '😬', '😮‍💨', '🤥', '😌', '😔', '😪', '🤤', '😴', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥵', '🥶', '🥴', '😵', '🤯', '🤠', '🥳', '🥸', '😎', '🤓', '🧐']
  },
  gestures: {
    label: 'Gestures',
    emojis: ['👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💪', '🦵', '🦶']
  },
  hearts: {
    label: 'Hearts',
    emojis: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '♥️', '🫶']
  },
  celebrations: {
    label: 'Celebrations',
    emojis: ['🎉', '🎊', '🎈', '🎂', '🎁', '🎄', '🎃', '🎇', '🎆', '✨', '🎵', '🎶', '🎤', '🎧', '🎸', '🎹', '🥁', '🎺', '🎷', '🪗', '🎻', '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🎗️', '🎪']
  },
  nature: {
    label: 'Nature',
    emojis: ['🌸', '💐', '🌷', '🌹', '🥀', '🌺', '🌻', '🌼', '🌱', '🪴', '🌲', '🌳', '🌴', '🌵', '🌾', '🌿', '☘️', '🍀', '🍁', '🍂', '🍃', '🌍', '🌎', '🌏', '🌕', '🌙', '⭐', '🌟', '✨', '☀️', '🌤️', '⛅', '🌈', '☔', '❄️', '🔥', '💧', '🌊']
  },
  food: {
    label: 'Food',
    emojis: ['🍕', '🍔', '🍟', '🌭', '🍿', '🧂', '🥓', '🥚', '🍳', '🧇', '🥞', '🧈', '🍞', '🥐', '🥖', '🥨', '🧀', '🥗', '🥙', '🌮', '🌯', '🥪', '🍱', '🍘', '🍙', '🍚', '🍛', '🍜', '🍝', '🍰', '🎂', '🧁', '🥧', '🍦', '🍧', '🍨', '🍩', '🍪', '☕', '🍵', '🧃', '🥤', '🍺', '🍷', '🥂', '🍾']
  },
  animals: {
    label: 'Animals',
    emojis: ['🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒', '🐔', '🐧', '🐦', '🐤', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🦟', '🦗', '🐢', '🐍', '🦎', '🐙', '🦑', '🦞', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊']
  },
  objects: {
    label: 'Objects',
    emojis: ['💡', '🔦', '🕯️', '📱', '💻', '⌨️', '🖥️', '🖨️', '📷', '📸', '📹', '🎥', '📺', '📻', '🎙️', '⏰', '⏱️', '⌚', '📿', '💎', '🔑', '🗝️', '🔒', '🔓', '📦', '📫', '📪', '📬', '📭', '📮', '✏️', '✒️', '🖊️', '🖋️', '📝', '📁', '📂', '📅', '📆', '📌', '📍', '✂️', '📐', '📏']
  }
};

/**
 * EmojiPicker Component
 *
 * A button that opens a popover with emoji categories for inserting emojis into text fields.
 *
 * @param {function} onEmojiSelect - Callback when an emoji is selected, receives the emoji string
 * @param {string} size - Button size: 'small', 'medium', 'large'
 * @param {object} sx - Additional styles for the button
 */
const EmojiPicker = ({ onEmojiSelect, size = 'small', sx = {} }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEmojiClick = (emoji) => {
    if (onEmojiSelect) {
      onEmojiSelect(emoji);
    }
    handleClose();
  };

  const open = Boolean(anchorEl);
  const categories = Object.values(EMOJI_CATEGORIES);

  return (
    <>
      <Tooltip title="Add emoji">
        <IconButton
          size={size}
          onClick={handleClick}
          sx={{ color: 'text.secondary', ...sx }}
        >
          <EmojiIcon fontSize={size} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Box sx={{ width: 320, maxHeight: 400 }}>
          {/* Category Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                minWidth: 'auto',
                px: 1.5,
                fontSize: '1.2rem'
              }
            }}
          >
            {categories.map((cat, index) => (
              <Tab key={cat.label} label={cat.emojis[0]} title={cat.label} />
            ))}
          </Tabs>

          {/* Emoji Grid */}
          <Box sx={{ p: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {categories[activeTab].label}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 0.5,
                maxHeight: 250,
                overflowY: 'auto'
              }}
            >
              {categories[activeTab].emojis.map((emoji, index) => (
                <Box
                  key={index}
                  onClick={() => handleEmojiClick(emoji)}
                  sx={{
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    textAlign: 'center',
                    p: 0.5,
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  {emoji}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default EmojiPicker;
